/* Scoped to the draft product. Dawn handles price, availability and cart variant. */
if (!customElements.get('cc-inside-message')) {
  customElements.define('cc-inside-message', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.closest('form');
      this.info = this.closest('product-info');
      this.toggle = this.querySelector('[data-inside-toggle]');
      this.fields = this.querySelector('[data-inside-fields]');
      this.message = this.querySelector('[data-inside-text]');
      this.nameField = this.form.querySelector('.cc-native-personalisation__input');
      this.status = this.querySelector('[data-inside-status]');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.quantityInput = this.info?.querySelector('input[name="quantity"]');
      this.multiCopyNote = this.querySelector('[data-multi-copy-note]');
      this.multiCopyTitle = this.querySelector('[data-multi-copy-title]');
      this.updateVariantPair();
      this.toggle.checked = this.variantInput.value === this.dataset.printed;
      this.updateFields();
      this.updateMultiCopyNote();
      this.onQuantityChange = () => this.updateMultiCopyNote();
      this.quantityInput?.addEventListener('change', this.onQuantityChange);
      this.quantityInput?.addEventListener('input', this.onQuantityChange);
      this.onSubmit = (event) => {
        // Validate the message first. A pending Dawn variant switch must never
        // bypass the required-message red field/error state.
        const requiredFields = [this.nameField, ...(this.toggle.checked ? [this.message] : [])].filter(field => field && !field.disabled && field.required);
        const emptyFields = requiredFields.filter(field => !field.value.trim());
        requiredFields.forEach(field => this.setFieldError(field, emptyFields.includes(field)));
        if (emptyFields.length) {
          event.preventDefault(); event.stopImmediatePropagation();
          emptyFields[0].focus({ preventScroll: true });
          emptyFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.updateVariantPair();
        const expected = this.toggle.checked ? this.dataset.printed : this.dataset.blank;
        if (this.variantInput.value !== expected) {
          event.preventDefault(); event.stopImmediatePropagation();
          if (this.toggle.checked) {
            this.status.hidden = false;
            this.status.textContent = 'Please wait for the price to update. If it does not update, refresh this page.';
          }
          return;
        }
      };
      this.form.addEventListener('submit', this.onSubmit, true);
      [this.nameField, this.message].filter(Boolean).forEach(field => {
        field.addEventListener('input', () => {
          if (field.value.trim()) this.setFieldError(field, false);
        });
      });
      this.toggle.addEventListener('change', () => this.chooseVariant());
      this.onVariant = () => {
        this.updateVariantPair();
        const expected = this.toggle.checked ? this.dataset.printed : this.dataset.blank;
        if (this.variantInput.value === expected) {
          this.status.hidden = !this.toggle.checked;
          this.status.textContent = this.toggle.checked ? this.insidePriceMessage() : '';
        }
      };
      this.variantInput.addEventListener('change', this.onVariant);
      this.onVariant();
      customElements.whenDefined('variant-selects').then(() => {
        if (this.isConnected && this.info?.querySelector('variant-selects')) this.toggle.disabled = false;
      });
    }
    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.variantInput?.removeEventListener('change', this.onVariant);
      this.quantityInput?.removeEventListener('change', this.onQuantityChange);
      this.quantityInput?.removeEventListener('input', this.onQuantityChange);
      this.ready = false;
    }
    updateVariantPair() {
      const picker = this.info?.querySelector('variant-selects');
      let selectedVariant;
      try {
        selectedVariant = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || '{}');
      } catch {
        selectedVariant = {};
      }
      this.insideCardCount = selectedVariant.option1 === 'Pack of 10' ? 10
        : selectedVariant.option1 === 'Pack of 20' ? 20 : 1;
      const label = this.toggle?.nextElementSibling;
      if (label) label.textContent = this.insideCardCount > 1
        ? `Add a printed message inside (+£${(1.5 * this.insideCardCount).toFixed(2)} per pack; £1.50 per card)`
        : 'Add a printed message inside (+£1.50)';
      this.updateMultiCopyNote();
      for (const kind of ['blank', 'printed']) {
        const source = picker?.querySelector(`[data-inside-${kind}]`);
        if (source) {
          try {
            const id = JSON.parse(source.textContent);
            this.dataset[kind] = id == null ? '' : String(id);
          } catch {
            this.dataset[kind] = '';
          }
        }
      }
    }
    insidePriceMessage() {
      return this.insideCardCount > 1
        ? `Your printed inside message adds £${(1.5 * this.insideCardCount).toFixed(2)} to this pack (£1.50 per card). Every card in the pack will use the same message.`
        : 'Your printed inside message adds £1.50 to the card price.';
    }
    updateMultiCopyNote() {
      if (!this.multiCopyNote) return;
      const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value || '1', 10) || 1);
      const copies = quantity * (this.insideCardCount || 1);
      this.multiCopyNote.hidden = !this.toggle.checked || copies < 2;
      if (this.multiCopyTitle) {
        this.multiCopyTitle.textContent = `${copies} cards will use the same message shown above.`;
      }
    }
    updateFields() {
      this.fields.hidden = !this.toggle.checked;
      this.message.disabled = !this.toggle.checked;
      this.message.required = this.toggle.checked;
      this.toggle.setAttribute('aria-expanded', String(this.toggle.checked));
      this.status.hidden = !this.toggle.checked;
      if (!this.toggle.checked) this.status.textContent = '';
      this.message.setCustomValidity('');
      this.setFieldError(this.message, false);
      this.updateMultiCopyNote();
    }
    setFieldError(field, invalid) {
      const errorId = `${field.id}-required-error`;
      field.classList.toggle('cc-personalisation-field--error', invalid);
      const descriptions = (field.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== errorId);
      let error = this.form.querySelector(`[id="${errorId}"]`);
      if (invalid) {
        field.setAttribute('aria-invalid', 'true');
        descriptions.push(errorId);
        if (!error) {
          error = document.createElement('p');
          error.id = errorId;
          error.className = 'cc-personalisation-error';
          error.setAttribute('role', 'alert');
          error.textContent = 'Please add your text before adding this card to your basket.';
          field.insertAdjacentElement('afterend', error);
        }
      } else {
        field.removeAttribute('aria-invalid');
        error?.remove();
      }
      if (descriptions.length) field.setAttribute('aria-describedby', descriptions.join(' '));
      else field.removeAttribute('aria-describedby');
    }
    chooseVariant() {
      if (!this.toggle.checked) this.message.value = '';
      this.updateFields();
      const value = this.toggle.checked ? 'Printed message inside' : 'Blank inside';
      const picker = this.info.querySelector('variant-selects');
      const option = Array.from(picker.querySelectorAll('input[type="radio"], option')).find(el => el.value === value);
      if (!option) {
        this.status.textContent = 'This option could not load. Please refresh the page.';
        return;
      }
      this.status.textContent = 'Updating your card price…';
      if (option.tagName === 'OPTION') {
        option.parentElement.value = value;
        option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        option.checked = true;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}
